/**
 * Zustand store for Storefront Settings
 * Persists to localStorage for backward compatibility.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const KEY = "khays_storefront_settings";

export interface StorefrontSettings {
  faqEnabled: boolean;
  faqTitle: string;
  gradeGuideEnabled: boolean;
  gradeGuideTitle: string;
  gradeGuideTitleColor: string;
  gradeGuideBgColor: string;
  gradeGuideBorderColor: string;
  gradeGuideIconColor: string;
  gradeGuideEffect: "none" | "glow" | "gradient" | "pulse";
  contactNoteEnabled: boolean;
  contactNoteHeading: string;
  contactNoteHeadingColor: string;
  contactNoteWhatsAppMessage: string;
  contactNoteCtaLabel: string;
  contactNoteCtaLabelColor: string;
  shippingDisabledCategories: string[];
  storefrontLocked: boolean;
  lockedStaffUsernames: string[];
  inquireLowerPriceEnabled: boolean;
  inquireLowerPriceDevices: string[];
  hiddenPriceCombos: Record<string, boolean>;
  /** Show/hide visitor chat widget */
  chatWidgetEnabled: boolean;
  /** Chat widget button color (hex) */
  chatButtonColor: string;
  /** Chat widget button position */
  chatButtonPosition: "bottom-right" | "bottom-left";
  /** Chat widget button icon color (hex) */
  chatButtonIconColor: string;
  /** Allow visitors to drag the chat button to any position. */
  chatButtonDraggable: boolean;
  /** Require customers to verify their phone number before placing orders. */
  requirePhoneVerification: boolean;
  /** Show "auto-excluded → tap to manage" hints on Modern Home for categories assigned to Available Now / Special Offers. */
  homeShowExclusionHints: boolean;
  /** Lite-theme-only lockdown: full block, bypass for Owner only. */
  liteLockdownEnabled: boolean;
  /** Heading shown on Lite lockdown screen. */
  liteLockdownTitle: string;
  /** Body message shown on Lite lockdown screen. */
  liteLockdownMessage: string;
  /** WhatsApp contact button label on Lite lockdown screen (empty hides button). */
  liteLockdownContactLabel: string;
  /** Require visitors to login/signup before accessing the storefront. */
  storefrontAuthRequired: boolean;
  /** Hard = block all storefront content. Soft = allow browsing, require auth on Buy. */
  storefrontAuthMode: "hard" | "soft";
  /** Show "Remember me" checkbox on the auth gate. */
  storefrontAuthAllowRemember: boolean;
  /** Show "Welcome back, {name}" toast for returning users on hard gate auto-pass. */
  storefrontAuthShowWelcomeBack: boolean;
  /** Show the "Continue with Google" button on customer sign-in / sign-up. */
  googleAuthEnabled: boolean;
  /** Storefront header brand title (super admin / admin editable). */
  siteTitle: string;
  /** Storefront header brand tagline / subtitle. */
  siteTagline: string;
}

const STOREFRONT_FIELDS: (keyof StorefrontSettings)[] = [
  "faqEnabled", "faqTitle", "gradeGuideEnabled", "gradeGuideTitle",
  "gradeGuideTitleColor", "gradeGuideBgColor", "gradeGuideBorderColor",
  "gradeGuideIconColor", "gradeGuideEffect",
  "contactNoteEnabled", "contactNoteHeading", "contactNoteHeadingColor",
  "contactNoteWhatsAppMessage", "contactNoteCtaLabel", "contactNoteCtaLabelColor",
  "shippingDisabledCategories", "storefrontLocked", "lockedStaffUsernames",
  "inquireLowerPriceEnabled", "inquireLowerPriceDevices", "hiddenPriceCombos",
  "chatWidgetEnabled", "chatButtonColor", "chatButtonPosition", "chatButtonIconColor",
  "chatButtonDraggable", "requirePhoneVerification", "homeShowExclusionHints",
  "liteLockdownEnabled", "liteLockdownTitle", "liteLockdownMessage", "liteLockdownContactLabel",
  "storefrontAuthRequired", "storefrontAuthMode", "storefrontAuthAllowRemember", "storefrontAuthShowWelcomeBack",
  "googleAuthEnabled",
  "siteTitle", "siteTagline",
];

function getDefaults(): StorefrontSettings {
  return {
    faqEnabled: true,
    faqTitle: "Frequently Asked Questions",
    gradeGuideEnabled: true,
    gradeGuideTitle: "What Do Device Grades Mean?",
    gradeGuideTitleColor: "",
    gradeGuideBgColor: "",
    gradeGuideBorderColor: "",
    gradeGuideIconColor: "",
    gradeGuideEffect: "none" as const,
    contactNoteEnabled: true,
    contactNoteHeading: "Can't find the device you're looking for?",
    contactNoteHeadingColor: "",
    contactNoteWhatsAppMessage: "Hi there👋\n\nI'm looking for a {category} device.\n{model}\n\nCan you help?",
    contactNoteCtaLabel: "Contact us on WhatsApp",
    contactNoteCtaLabelColor: "",
    shippingDisabledCategories: [],
    storefrontLocked: false,
    lockedStaffUsernames: [],
    inquireLowerPriceEnabled: true,
    inquireLowerPriceDevices: [],
    hiddenPriceCombos: {},
    chatWidgetEnabled: true,
    chatButtonColor: "",
    chatButtonPosition: "bottom-right" as const,
    chatButtonIconColor: "",
    chatButtonDraggable: true,
    requirePhoneVerification: false,
    homeShowExclusionHints: true,
    liteLockdownEnabled: false,
    liteLockdownTitle: "We'll be right back",
    liteLockdownMessage: "Our store is briefly closed for maintenance. Please check back shortly or contact us on WhatsApp.",
    liteLockdownContactLabel: "Contact us on WhatsApp",
    storefrontAuthRequired: false,
    storefrontAuthMode: "soft" as const,
    storefrontAuthAllowRemember: true,
    storefrontAuthShowWelcomeBack: true,
    googleAuthEnabled: true,
    siteTitle: "Khay's Phones & Premium Devices",
    siteTagline: "Quality-inspected. Pre-order ready.",
  };
}

/** Strip any fields not in the StorefrontSettings interface to prevent data bloat */
function sanitizeSettings(raw: Record<string, unknown>): StorefrontSettings {
  const defaults = getDefaults();
  const clean: Record<string, unknown> = {};
  for (const key of STOREFRONT_FIELDS) {
    clean[key] = key in raw ? raw[key] : defaults[key];
  }
  return clean as unknown as StorefrontSettings;
}

function loadFromStorage(): StorefrontSettings {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored) return sanitizeSettings(JSON.parse(stored));
  } catch {}
  return getDefaults();
}

interface StorefrontSettingsState extends StorefrontSettings {
  /** Replace all settings */
  setSettings: (settings: StorefrontSettings) => void;
  /** Patch specific fields */
  patch: (partial: Partial<StorefrontSettings>) => void;
  /** Reload from localStorage (useful after external writes) */
  reload: () => void;
}

export const useStorefrontSettings = create<StorefrontSettingsState>()(
  devtools(
    (set, get) => ({
      ...loadFromStorage(),

  setSettings: (settings) => {
    localStorage.setItem(KEY, JSON.stringify(settings));
    set({ ...settings });
    try { window.dispatchEvent(new CustomEvent("khays:storefront-settings-changed")); } catch {}
  },

  patch: (partial) => {
    const { setSettings, patch, reload, ...current } = get();
    const next = { ...current, ...partial };
    localStorage.setItem(KEY, JSON.stringify(next));
    set(partial);
    try { window.dispatchEvent(new CustomEvent("khays:storefront-settings-changed")); } catch {}
  },

  reload: () => {
    set(loadFromStorage());
  },
    }),
    { name: "StorefrontSettings" }
  )
);

// Cross-tab + post-cloud-sync live updates so the auth-gate (and any other
// settings-driven UI) reflects changes immediately without manual refresh.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) useStorefrontSettings.getState().reload();
  });
  window.addEventListener("khays:storefront-settings-changed", () => {
    useStorefrontSettings.getState().reload();
  });
}

// ─── Backward-compatible exports ───────────────────────────────
// These allow existing code to keep working unchanged.
// As components migrate to `useStorefrontSettings`, these can be removed.

export function loadStorefrontSettings(): StorefrontSettings {
  return useStorefrontSettings.getState();
}

export function saveStorefrontSettings(settings: StorefrontSettings): void {
  useStorefrontSettings.getState().setSettings(settings);
}

export function isShippingEnabledForCategory(category: string): boolean {
  const s = useStorefrontSettings.getState();
  return !s.shippingDisabledCategories.includes(category);
}
